import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export type CheckoutPaymentMethod = 'cash' | 'mpesa' | 'pay_later';
export type MpesaPaymentMode = 'STK' | 'PAID_TO_TILL';

export interface CheckoutPaymentIntent {
  method: CheckoutPaymentMethod;
  cashAmount: string;
  mpesa: {
    phone: string;
    mode: MpesaPaymentMode;
    stkStatus: 'idle' | 'sent' | 'error';
    lastRequestedAt?: string;
  };
}

export interface CheckoutPaymentSelection {
  cash: boolean;
  mpesa: boolean;
  payLater: boolean;
}

export interface CheckoutSliceObject {
  checkout: CheckoutItem[];
  paymentIntent: CheckoutPaymentIntent;
  paymentSelection: CheckoutPaymentSelection;
}

export interface CheckoutItem {
  bp_product_id: string;
  bp_variant_id?: string;
  bp_order_price: number;
  bp_order_quantity: number;
  bp_order_mass: number | null;
}

const createInitialPaymentIntent = (): CheckoutPaymentIntent => ({
  method: 'cash',
  cashAmount: '',
  mpesa: {
    phone: '',
    mode: 'STK',
    stkStatus: 'idle',
    lastRequestedAt: undefined,
  },
});

const createInitialPaymentSelection = (): CheckoutPaymentSelection => ({
  cash: true,
  mpesa: false,
  payLater: false,
});

const initialState: CheckoutSliceObject = {
  checkout: [],
  paymentIntent: createInitialPaymentIntent(),
  paymentSelection: createInitialPaymentSelection(),
};

type CheckoutPaymentIntentPatch = Partial<
  Omit<CheckoutPaymentIntent, 'mpesa'>
> & {
  mpesa?: Partial<CheckoutPaymentIntent['mpesa']>;
};

const checkoutSlice = createSlice({
  name: 'checkout',
  initialState,
  reducers: {
    addToCheckout: (state, action: PayloadAction<CheckoutItem[]>) => {
      state.checkout = action.payload;
    },
    clearCheckout: state => {
      state.checkout = [];
    },
    setCheckoutPaymentIntent: (
      state,
      action: PayloadAction<CheckoutPaymentIntentPatch>,
    ) => {
      state.paymentIntent = {
        ...state.paymentIntent,
        ...action.payload,
        mpesa: {
          ...state.paymentIntent.mpesa,
          ...(action.payload.mpesa ?? {}),
        },
      };
    },
    resetCheckoutPaymentIntent: state => {
      state.paymentIntent = createInitialPaymentIntent();
      state.paymentSelection = createInitialPaymentSelection();
    },
    setCheckoutPaymentSelection: (
      state,
      action: PayloadAction<Partial<CheckoutPaymentSelection>>,
    ) => {
      state.paymentSelection = {
        ...state.paymentSelection,
        ...action.payload,
      };
    },
  },
});

export const {
  addToCheckout,
  clearCheckout,
  setCheckoutPaymentIntent,
  resetCheckoutPaymentIntent,
  setCheckoutPaymentSelection,
} = checkoutSlice.actions;
export default checkoutSlice.reducer;
